/**
 * Weekly session completion ring.
 * Shows completed / planned sessions for the current week.
 */

import { useTranslation } from 'react-i18next';
import { GlassCard } from '@/components/ui/GlassCard';
import { CardContent } from '@/components/ui/layout/card';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { fetchWeeklyCompletion, type WeeklyCompletionData } from '@/lib/analyticsApi';

const RING_SIZE = 48;
const STROKE_WIDTH = 4;
const RADIUS = (RING_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function CompletionRing({ percentage }: { percentage: number }) {
  const offset = CIRCUMFERENCE - (percentage / 100) * CIRCUMFERENCE;
  const color = percentage >= 100 ? '#00FFC8' : percentage >= 50 ? '#FFB43C' : 'rgba(255,255,255,0.2)';

  return (
    <svg width={RING_SIZE} height={RING_SIZE} className="shrink-0 -rotate-90">
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RADIUS}
        fill="none"
        stroke="rgba(255,255,255,0.06)"
        strokeWidth={STROKE_WIDTH}
      />
      <circle
        cx={RING_SIZE / 2}
        cy={RING_SIZE / 2}
        r={RADIUS}
        fill="none"
        stroke={color}
        strokeWidth={STROKE_WIDTH}
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="transition-all duration-700 ease-out"
      />
    </svg>
  );
}

export function WeeklyCompletionRing() {
  const { t } = useTranslation();

  const { data, isLoading } = useAuthenticatedQuery<WeeklyCompletionData>({
    queryKey: ['analytics', 'weeklyCompletion'],
    queryFn: fetchWeeklyCompletion,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  if (isLoading || !data) return null;

  // Don't show if no planned sessions this week
  if (data.planned === 0) return null;

  return (
    <GlassCard className="border-white/[0.06]">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="relative">
          <CompletionRing percentage={data.percentage} />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs font-mono font-semibold text-foreground">
              {data.completed}/{data.planned}
            </span>
          </div>
        </div>

        <div className="min-w-0">
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground block">
            {t('dashboard.weeklyCompletion.title', { defaultValue: 'This Week' })}
          </span>
          <span className="text-xs text-muted-foreground/70 font-mono">
            {t('dashboard.weeklyCompletion.detail', {
              defaultValue: '{{pct}}% complete',
              pct: Math.round(data.percentage),
            })}
          </span>
        </div>
      </CardContent>
    </GlassCard>
  );
}
